import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Star } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@satyrsmc/shared/components";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useMemberEventDetail } from "@/queries/member-events";
import { useMyEventFeedback, useSubmitEventFeedback } from "@/queries/event-feedback";
import { isEventPast } from "@/lib/eventUtils";

const RATINGS = [1, 2, 3, 4, 5] as const;
const MAX_COMMENT_LENGTH = 2000;

interface RatingPickerProps {
  value: number | null;
  onChange: (rating: number) => void;
}

function RatingPicker({ value, onChange }: RatingPickerProps) {
  return (
    <div role="radiogroup" aria-label="Rating" className="flex gap-1">
      {RATINGS.map((rating) => {
        const filled = value !== null && rating <= value;
        return (
          <button
            key={rating}
            type="button"
            role="radio"
            aria-checked={value === rating}
            aria-label={`${rating} star${rating === 1 ? "" : "s"}`}
            onClick={() => onChange(rating)}
            className="rounded p-1 text-muted-foreground hover:text-amber-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <Star
              className={`size-7 ${filled ? "fill-amber-400 text-amber-400" : ""}`}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Standalone feedback page for an event — the URL is emailed to attendees, so
 * it works as a direct entry point. One row per member per event: visiting
 * again prefills whatever they last said and submitting again edits it rather
 * than adding a second entry.
 */
export function EventFeedbackPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: event, isLoading: eventLoading, error: eventError } = useMemberEventDetail(id!);

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  const { data: existing, isLoading } = useMyEventFeedback(id!, true);
  const submitFeedback = useSubmitEventFeedback();

  // Prefill from the saved answers once the query resolves, so an edit starts
  // from what the member said last time.
  useEffect(() => {
    setRating(existing?.rating ?? null);
    setComment(existing?.comment ?? "");
  }, [existing]);

  if (eventLoading) {
    return (
      <PageContainer>
        <div className="mx-auto max-w-md space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </PageContainer>
    );
  }

  if (eventError || !event) {
    return (
      <PageContainer>
        <Link
          to="/events"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-4" />
          Back to Events
        </Link>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Event not found.
        </div>
      </PageContainer>
    );
  }

  // Mirrors how the event detail sidebar gates its "How was it?" button — the
  // server rejects ineligible submits anyway; this just keeps a direct visit
  // (e.g. from an emailed link) friendly.
  if (!isEventPast(event) || !event.can_contribute) {
    return (
      <PageContainer>
        <div className="mx-auto max-w-md space-y-4">
          <h1 className="text-2xl font-bold">{event.name}</h1>
          <p className="text-sm text-muted-foreground">Feedback isn't open for this event.</p>
          <Button variant="outline" asChild>
            <Link to={`/events/${event.id}`}>Back to event</Link>
          </Button>
        </div>
      </PageContainer>
    );
  }

  function handleSubmit() {
    if (rating === null) return;
    submitFeedback.mutate(
      { event_id: id!, rating, comment: comment.trim() || null },
      {
        onSuccess: () => {
          toast.success("Thanks for the feedback!");
          navigate(`/events/${id}`);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  }

  return (
    <PageContainer>
      <div className="mx-auto max-w-md space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">How was it?</h1>
          <p className="text-sm text-muted-foreground">
            Rate {event.name} and tell us anything the club should know for next time.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Your rating</p>
            <RatingPicker value={rating} onChange={setRating} />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="event-feedback-comment">
              Comments <span className="text-muted-foreground">(optional)</span>
            </label>
            <Textarea
              id="event-feedback-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={MAX_COMMENT_LENGTH}
              rows={4}
              placeholder="What worked, what didn't?"
            />
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={rating === null || isLoading || submitFeedback.isPending}
          className="gap-1.5"
        >
          {submitFeedback.isPending && <Loader2 className="size-4 animate-spin" />}
          {submitFeedback.isPending ? "Saving…" : "Submit feedback"}
        </Button>
      </div>
    </PageContainer>
  );
}
